import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import StatCounter from "./StatCounter";
import { STATS, DOMAINS, AI_SOLUTIONS, DEPLOYMENT_MODELS, SUPPORTED_MODELS, SERVICES, UNIDAF_FEATURES, CLIENT_LOGOS, COMPANY, GESIL_LOGO_URL } from "../../data/content";
import { ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import Section from "./Section";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { resolveImageUrl } from "../admin/ImageUpload";

// ---------- About + Stats (CMS-driven, fallback to static) ----------
export function AboutSection() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/site-settings").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  const eyebrow = cfg?.about_subtitle || "About GESIL";
  const title = cfg?.about_title || "Driving Digital Governance Across India.";
  const desc = cfg?.about_description || "Global e-Village Services India Ltd is a leading GovTech company delivering transformative digital solutions for Governments, PSUs and Development Agencies. We specialize in Digital Governance, Artificial Intelligence, Enterprise Platforms, Municipal Transformation and Citizen-Centric Service Delivery.";
  const stats = cfg
    ? [
        { label: cfg.stat_1_label, value: cfg.stat_1_value, suffix: cfg.stat_1_suffix },
        { label: cfg.stat_2_label, value: cfg.stat_2_value, suffix: cfg.stat_2_suffix },
        { label: cfg.stat_3_label, value: cfg.stat_3_value, suffix: cfg.stat_3_suffix },
        { label: cfg.stat_4_label, value: cfg.stat_4_value, suffix: cfg.stat_4_suffix },
      ]
    : STATS;
  return (
    <div className="relative bg-white dark:bg-[#0B1B40] overflow-hidden">
      <div className="absolute inset-0 bg-dots-blue opacity-50 pointer-events-none" />
      <div className="absolute top-32 -left-20 w-80 h-80 blob blob-blue opacity-30 pointer-events-none" />
      <Section id="about" eyebrow={eyebrow} title={title} subtitle={desc}>
        <div className="grid md:grid-cols-4 gap-10 md:gap-6 border-t border-blue-100 dark:border-blue-800 pt-12">
          {stats.map((s, i) => (
            <motion.div key={s.label || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }} data-testid={`stat-${i}`}>
              <div className="overline mb-4">0{i + 1}</div>
              <StatCounter value={s.value} suffix={s.suffix} />
              <div className="mt-4 text-[#475569] dark:text-blue-100 text-sm uppercase tracking-[0.15em]">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ---------- Domain Expertise (full govt coverage) ----------
export function DomainSection() {
  const items = useCMSList("domains", DOMAINS);
  return (
    <section id="expertise" data-testid="section-expertise" className="relative bg-mist py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-blue opacity-50 pointer-events-none" />
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-blue-300/40 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="section-overline">Government Domains</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mt-5 leading-tight text-balance text-[#0F172A]">
            Where GESIL has delivered for India&apos;s governments.
          </h2>
          <p className="mt-6 text-[#475569] text-lg leading-relaxed">
            A full-spectrum coverage of departments and missions across state and central governments —
            click any domain to see the matching projects below.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((d, i) => {
            const Icon = Icons[d.icon] || Icons.Sparkles;
            const href = d.tag
              ? `/?tag=${encodeURIComponent(d.tag)}#projects`
              : "/#projects";
            return (
              <motion.div
                key={d.id || d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.06 }}
                data-testid={`domain-${i}`}
              >
                <Link
                  to={href}
                  data-testid={`domain-link-${i}`}
                  className="group block p-6 rounded-2xl bg-white border border-blue-100 hover:border-[#2563EB] hover:-translate-y-1 hover:shadow-[0_30px_50px_-30px_rgba(37,99,235,0.35)] transition-all h-full"
                >
                  <div className="h-11 w-11 inline-flex items-center justify-center rounded-md bg-blue-50 text-[#2563EB] group-hover:bg-[#2563EB] group-hover:text-white transition-colors">
                    <Icon size={20} />
                  </div>
                  <div className="mt-5 font-display text-lg font-semibold text-[#0F172A] leading-snug">{d.title}</div>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#475569]">
                    {(d.items || []).slice(0, 3).map((x) => (
                      <li key={x} className="flex items-start gap-2"><CheckCircle2 size={13} className="text-[#2563EB] shrink-0 mt-1" /><span>{x}</span></li>
                    ))}
                    {!d.items?.length && d.description && (
                      <li className="text-[#475569]">{d.description}</li>
                    )}
                    {(d.items?.length || 0) > 3 && (
                      <li className="text-xs text-[#2563EB] font-semibold pt-1">+ {d.items.length - 3} more</li>
                    )}
                  </ul>
                  {d.tag && (
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#2563EB] uppercase tracking-wider">
                      View projects <ArrowUpRight size={12} />
                    </div>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- AI Solutions (CMS-driven, fallback to static) ----------
function useCMSList(kind, fallback) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/content-lists/${kind}`).then((r) => Array.isArray(r.data) && r.data.length && setItems(r.data)).catch(() => {}); }, [kind]);
  return items || fallback;
}

export function AISolutionsSection() {
  const aiList = useCMSList("ai_solutions", AI_SOLUTIONS.map((s) => ({ id: s, title: s })));
  return (
    <section id="ai" data-testid="section-ai" className="relative bg-white text-[#0F172A] py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-blue opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="section-overline">AI Solutions</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mt-5 leading-tight text-balance">
              Reimagine Government Productivity with AI.
            </h2>
            <p className="mt-5 font-display text-xl md:text-2xl italic text-[#2563EB]">
              Secure. Explainable. Sovereign AI.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <a href="#unidaf" className="inline-flex items-center gap-2 text-[#0F172A] font-semibold border-b-2 border-[#2563EB] pb-1 hover:gap-3 transition-all">
              Meet UniDAF <ArrowUpRight size={18} />
            </a>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-3">
          {aiList.map((s, i) => (
            <motion.div
              key={s.id || s.title || i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              data-testid={`ai-solution-${i}`}
              className="group bg-white border border-blue-100 rounded-lg p-5 flex items-center justify-between hover:border-[#2563EB] hover:shadow-[0_24px_48px_-24px_rgba(37,99,235,0.4)] transition-all"
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-md bg-blue-50 text-[#2563EB] inline-flex items-center justify-center group-hover:bg-[#2563EB] group-hover:text-white transition-colors">
                  <Sparkles size={16} />
                </span>
                <span className="font-medium">{s.title || s}</span>
              </div>
              <ArrowUpRight size={16} className="text-blue-300 group-hover:text-[#2563EB]" />
            </motion.div>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-blue-100 p-8 bg-soft-blue">
            <div className="overline mb-4">Deployment Models</div>
            <div className="flex flex-wrap gap-2">
              {DEPLOYMENT_MODELS.map((m) => (
                <span key={m} className="px-4 py-2 rounded-full bg-[#2563EB] text-white text-sm font-semibold">{m}</span>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-blue-100 p-8 bg-soft-blue">
            <div className="overline mb-4">Supported Models</div>
            <div className="flex flex-wrap gap-2">
              {SUPPORTED_MODELS.map((m) => (
                <span key={m} className="px-4 py-2 rounded-full border border-[#2563EB] text-[#1E3A8A] bg-white text-sm font-semibold">{m}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Featured UniDAF ----------
export function UnidafSection() {
  return (
    <section id="unidaf" data-testid="section-unidaf" className="relative py-24 md:py-32 bg-azure-mesh overflow-hidden">
      <div className="absolute inset-0 bg-grid-blue opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="section-overline">Featured AI Product</div>
          <h2 className="font-display text-5xl md:text-6xl font-semibold mt-5 leading-[1.05] text-[#0F172A]">
            UniDAF<span className="text-[#2563EB]">™</span>
          </h2>
          <p className="mt-4 text-[#475569] text-lg font-display italic">
            Unified Intelligent Digital Assistant Framework
          </p>
          <p className="mt-6 text-[#475569] leading-relaxed">
            An enterprise-grade, sovereign AI platform purpose-built for Indian government departments
            and large institutions. Bring your data, choose your model, and ship grounded AI assistants
            in weeks — not quarters.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#contact" data-testid="unidaf-demo-cta" className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-7 py-4 text-sm font-semibold uppercase tracking-wide shadow-[0_18px_40px_-18px_rgba(37,99,235,0.7)]">
              Request Demonstration <ArrowUpRight size={18} />
            </a>
            <a href="#ai" className="inline-flex items-center gap-2 rounded-full border-2 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white px-7 py-4 text-sm font-semibold uppercase tracking-wide">
              AI Capabilities
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {UNIDAF_FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="bg-white border border-blue-100 hover:border-[#2563EB] rounded-xl p-6 transition-colors hover:shadow-[0_24px_48px_-24px_rgba(37,99,235,0.3)]"
            >
              <div className="font-mono-display text-xs text-[#2563EB]">0{i + 1}</div>
              <div className="font-display text-xl mt-3 text-[#0F172A]">{f.title}</div>
              <div className="mt-2 text-sm text-[#475569] leading-relaxed">{f.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Services ----------
export function ServicesSection() {
  const services = useCMSList("services", SERVICES);
  return (
    <div className="relative bg-white overflow-hidden">
      <div className="absolute inset-0 bg-hatch-blue opacity-70 pointer-events-none" />
      <div className="absolute top-1/2 -right-32 w-96 h-96 blob blob-cyan opacity-30 pointer-events-none" />
      <Section id="services" eyebrow="Services" title="An end-to-end practice for the public sector." subtitle="From strategy and architecture to engineering, AI and managed services — we deliver across the full government technology stack.">
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-blue-100 rounded-xl overflow-hidden border border-blue-100">
        {services.map((s, i) => {
          const Icon = Icons[s.icon] || Icons.Sparkles;
          return (
            <motion.div
              key={s.id || s.title}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.03 }}
              data-testid={`service-${i}`}
              className="group bg-white p-7 hover:bg-blue-50 transition-colors cursor-pointer relative"
            >
              <div className="h-12 w-12 inline-flex items-center justify-center rounded-md bg-blue-50 text-[#2563EB] group-hover:bg-[#2563EB] group-hover:text-white transition-colors">
                <Icon size={20} />
              </div>
              <div className="mt-5 font-display text-lg font-semibold text-[#0F172A]">{s.title}</div>
              <ArrowUpRight size={16} className="absolute top-7 right-7 text-blue-300 group-hover:text-[#2563EB] transition-colors" />
            </motion.div>
          );
        })}
      </div>
    </Section>
    </div>
  );
}

// ---------- Clients Wall (CMS-driven) ----------
export function ClientsSection() {
  const [clients, setClients] = useState(null);
  useEffect(() => {
    api.get("/clients")
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setClients(r.data); })
      .catch(() => {});
  }, []);
  const list = clients || CLIENT_LOGOS.map((name, i) => ({ id: i, name, logo_url: "", website_url: "" }));
  const row = [...list, ...list];
  return (
    <section id="clients" data-testid="section-clients" className="bg-soft-blue dark:bg-[#0B1B40] py-20 border-y border-blue-100 dark:border-blue-800 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="section-overline justify-center inline-flex">Our Clients</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold mt-4 text-[#0F172A] dark:text-white">Government departments, PSUs and institutions</h2>
          <p className="mt-4 text-[#475569] dark:text-blue-100 text-sm md:text-base">Trusted by governments, PSUs, municipal bodies and development authorities across India.</p>
        </div>
      </div>
      <div className="mt-12 relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#EFF6FF] dark:from-[#0B1B40] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#EFF6FF] dark:from-[#0B1B40] to-transparent z-10 pointer-events-none" />
        <div className="flex w-max animate-marquee gap-6">
          {row.map((c, i) => {
            const inner = (
              <div className="flex items-center gap-3 min-w-[220px]">
                {c.logo_url ? (
                  <img
                    src={resolveImageUrl(c.logo_url)}
                    alt={c.name}
                    loading="lazy"
                    className="h-10 w-10 md:h-12 md:w-12 object-contain rounded-md bg-white dark:bg-white p-1"
                  />
                ) : (
                  <div className="h-10 w-10 md:h-12 md:w-12 flex items-center justify-center rounded-md bg-blue-50 dark:bg-blue-900 text-[#1E3A8A] dark:text-blue-200 font-display text-lg font-bold shrink-0">
                    {(c.name || "?").charAt(0)}
                  </div>
                )}
                <span className="font-display text-sm md:text-base text-[#1E3A8A] dark:text-blue-100 leading-tight">
                  {c.name}
                </span>
              </div>
            );
            const box = (
              <div key={i} className="shrink-0 px-5 py-3 rounded-md border border-blue-200 dark:border-blue-800 bg-white dark:bg-[#0F172A] hover:border-[#2563EB] transition-colors">
                {inner}
              </div>
            );
            return c.website_url ? (
              <a key={i} href={c.website_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-90">{box}</a>
            ) : box;
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- Contact ----------
export function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "", interest: "Book a Consultation", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success("Thanks! Our team will reach out shortly.");
      setForm({ name: "", email: "", organization: "", phone: "", interest: "Book a Consultation", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="section-contact" className="relative bg-mist text-[#0F172A] py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-blue opacity-50" />
      <div className="absolute right-0 -bottom-32 h-96 w-96 rounded-full bg-blue-300/40 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="section-overline">Get in Touch</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mt-5 leading-tight text-balance">
            Let&apos;s design your next public sector transformation.
          </h2>
          <p className="mt-6 text-[#475569] text-lg leading-relaxed max-w-md">
            Book a consultation with our GovTech and AI advisors, or request a UniDAF demonstration tailored
            to your department.
          </p>

          <div className="mt-10 space-y-6">
            <div className="bg-white rounded-xl p-6 border border-blue-100">
              <div className="overline">{COMPANY.head_office.label}</div>
              <div className="mt-2 text-[#0F172A]">{COMPANY.head_office.address}</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-blue-100">
              <div className="overline">{COMPANY.corporate_office.label}</div>
              <div className="mt-2 text-[#0F172A]">{COMPANY.corporate_office.address}</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5 border border-blue-100">
                <div className="overline">Email</div>
                <a href={`mailto:${COMPANY.email_primary}`} className="block mt-2 text-[#0F172A] hover:text-[#2563EB]">{COMPANY.email_primary}</a>
                <a href={`mailto:${COMPANY.email_secondary}`} className="block text-[#0F172A] hover:text-[#2563EB]">{COMPANY.email_secondary}</a>
              </div>
              <div className="bg-white rounded-xl p-5 border border-blue-100">
                <div className="overline">Toll Free</div>
                <div className="mt-2 font-display text-2xl text-[#1E3A8A]">{COMPANY.toll_free}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="lead-form"
            className="bg-white rounded-2xl p-8 md:p-10 space-y-5 border border-blue-100 shadow-[0_30px_80px_-40px_rgba(37,99,235,0.35)]"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full Name *" testid="lead-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="Work Email *" testid="lead-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Organisation / Department" testid="lead-org" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} />
              <Field label="Phone" testid="lead-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>
            <div>
              <label className="overline">Interest</label>
              <select
                data-testid="lead-interest"
                value={form.interest}
                onChange={(e) => setForm({ ...form, interest: e.target.value })}
                className="mt-2 w-full bg-white border-b border-blue-200 focus:border-[#2563EB] py-3 outline-none text-[#0F172A]"
              >
                {["Book a Consultation", "Request UniDAF Demo", "Partnership", "Careers Enquiry", "General"].map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline">How can we help? *</label>
              <textarea
                data-testid="lead-message"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-2 w-full bg-white border-b border-blue-200 focus:border-[#2563EB] py-3 outline-none text-[#0F172A] resize-none"
                placeholder="Tell us about your initiative..."
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-7 py-4 text-sm font-semibold uppercase tracking-wide disabled:opacity-60 shadow-[0_18px_40px_-18px_rgba(37,99,235,0.7)]"
            >
              {loading ? "Sending..." : "Send Enquiry"} <ArrowUpRight size={18} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="overline">{label}</label>
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full bg-white border-b border-blue-200 focus:border-[#2563EB] py-3 outline-none text-[#0F172A] placeholder:text-blue-300"
    />
  </div>
);
